/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { chunk } from 'lodash';
import { useTranslation } from 'next-i18next';
import React, {
    ForwardRefRenderFunction,
    Fragment,
    ReactNode,
    Ref,
    forwardRef,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { OtherStock } from '@toucan/common/features/recommendations';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { useIsomorphicLayoutEffect } from '@toucan/client/global/nextjs';

import { scrollToElement } from '@toucan/client/core/scroll';

import {
    ConstrainedWrapper,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { ScrollableCarousel } from '@toucan/client/shared/components/scrollable-carousel';

import { ButtonDivider } from '@toucan/client/blocks/components/button-divider';
import { Carousel } from '@toucan/client/blocks/components/carousel';
import { TruncateText } from '@toucan/client/blocks/components/truncate-text';

import {
    Definition,
    DefinitionList,
} from '@toucan/client/features/model/components/model-overview/components/definition-list';
import { useProductComparison } from '@toucan/client/features/model/components/model-overview/hooks/use-product-comparison';

import { MiniProductCard } from './components/mini-product-card';
import { DEFAULT_TEST_ID } from './constants';
import { styles } from './model-overview.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export const IMAGE_ASPECT_RATIO = 0.75;

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type ModelOverviewProps = {
    isLoading?: boolean;

    /**
     * Title of the model overview section
     */
    title: ReactNode;

    /**
     * Description for the model overview section
     */
    description: ReactNode;

    /**
     * Key features of the model
     */
    keyFeatures: ReactNode;

    /**
     * An array of model specs
     */
    specs: Definition[];

    /**
     * Images to show in the carousel
     */
    images?: ReactNode[];
    modelUrlSegment: string;
    modelName: string;
    otherStock?: OtherStock;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledModelOverviewProps = {
    hasImages: boolean;
    hasSpecs: boolean;
    isLoadingImages: boolean;
};

const blacklistedProps = ['title'];
const StyledModelOverview = styled('section').withConfig({
    shouldForwardProp: (prop) =>
        isPropValid(prop) && !blacklistedProps.includes(prop),
})`
    ${styles}
`;

/**
 * Gets the specs lists to be displayed on the page
 *
 * @param specs The specs to put in a list(s)
 * @param isSmallTablet If the viewport is small tablet sized
 *
 * NOTE: This is placed outside the ModelOverview component to keep
 * the linting happy
 */
function getSpecsLists(specs: Definition[], isSmallTablet: boolean) {
    // Number of specs before showing them in 2 columns
    const SPECS_MULTI_COLUMN_COUNT = 15;

    const columns =
        !isSmallTablet && specs.length > SPECS_MULTI_COLUMN_COUNT ? 2 : 1;
    const specsChunks = chunk(
        specs,
        columns === 1 ? specs.length : Math.ceil(specs.length / 2)
    );

    return (
        <Fragment>
            {specsChunks.map((specs, i) => {
                return (
                    <DefinitionList
                        definitions={specs}
                        // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                        key={i}
                        className="model-overview-specs-dl"
                        data-testid="model-overview__specs"
                    />
                );
            })}
        </Fragment>
    );
}

const ModelOverviewWithRef: ForwardRefRenderFunction<
    HTMLDivElement,
    ModelOverviewProps
> = (
    {
        'data-testid': dataTestId = DEFAULT_TEST_ID,
        isLoading = false,
        specs,
        title,
        images,
        description,
        keyFeatures,
        modelUrlSegment,
        modelName,
        otherStock,
        ...otherProps
    }: ModelOverviewProps,
    ref: Ref<HTMLDivElement>
) => {
    const { t } = useTranslation();
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const specsRef = useRef<HTMLDivElement>(null);
    const [shouldShowSpecs, setShouldShowSpecs] = useState<boolean>(false);
    const { setProductComparison, showProductComparison } =
        useProductComparison();
    const currentProduct = otherStock?.find(
        ({ isCurrentProduct }) => isCurrentProduct
    );

    useEffect(() => {
        if (!otherStock?.length || !currentProduct) {
            return;
        }

        setProductComparison(
            otherStock.map((product) => product.id),
            {
                id: currentProduct.id,
                name: modelName,
                urlSegment: modelUrlSegment,
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [currentProduct, otherStock, modelName, modelUrlSegment]);

    const isLoadingImages = !images;
    const hasSpecs = specs.length !== 0;
    const hasImages = (images?.length || 0) > 0;
    const paginationItems = images?.map((ignore, i) => ({
        text: (i + 1).toString(),
    }));

    /**
     * Scroll to the center of the specs section when the show/hide button is
     * clicked.
     */
    function scrollToSpecSection() {
        scrollToElement(specsRef.current);
    }

    const renderCarousel = () => {
        if (isLoadingImages) {
            return (
                <div className="model-overview-carousel">
                    <Skeleton
                        variant={SkeletonVariant.BLOCK}
                        className="model-overview-skeleton"
                    />
                </div>
            );
        }

        if (!hasImages) {
            return null;
        }

        return (
            <Carousel
                className="model-overview-carousel"
                items={images}
                paginationItems={paginationItems}
                showPagination={images?.length > 1}
                data-testid="model-overview__carousel"
            />
        );
    };

    /**
     * Renders the spec section
     */
    const renderSpecSection = () => {
        if (!hasSpecs) {
            return <div className="model-overview-specs-line" />;
        }

        if (!shouldShowSpecs) {
            return (
                <ButtonDivider
                    className="model-overview-specs-button"
                    onClick={() => setShouldShowSpecs(true)}
                    data-testid="model-overview__show-specs"
                >
                    {t('See full specs', { context: 'modelOverview' })}
                </ButtonDivider>
            );
        }

        return (
            <Fragment>
                <ConstrainedWrapper>
                    <Heading
                        size={HeadingSize.H2}
                        className="model-overview-specs-title"
                    >
                        {t('Full specs', { context: 'modelOverview' })}
                    </Heading>
                    <div className="model-overview-specs-dls">
                        {getSpecsLists(specs, isSmallTablet)}
                    </div>
                </ConstrainedWrapper>
                <ButtonDivider
                    className="model-overview-specs-button"
                    onClick={() => setShouldShowSpecs(false)}
                    data-testid="model-overview__hide-specs"
                >
                    {t('Hide full specs', { context: 'modelOverview' })}
                </ButtonDivider>
            </Fragment>
        );
    };

    const modelOverviewHeading = (
        <Heading size={HeadingSize.H2} className="model-overview-heading">
            {t('Model Overview', { context: 'modelOverview' })}
        </Heading>
    );

    /**
     * Scroll section into view once dimensions have been recomputed.
     */
    useIsomorphicLayoutEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- Legacy code: do not use as an example.
        shouldShowSpecs && scrollToSpecSection();
    }, [shouldShowSpecs]);

    const renderDescription = () => {
        if (isSmallTablet) {
            return (
                <TruncateText lines={4} isLoading={isLoading}>
                    {description}
                </TruncateText>
            );
        }

        return <div className="model-overview-description">{description}</div>;
    };

    const renderOtherStockProducts = () => {
        if (!otherStock?.length) {
            return null;
        }

        return (
            <div
                className="model-overview-other-stock"
                data-testid={`${dataTestId}__other-stock`}
            >
                <strong>{t('More items in stock')}</strong>
                <p>
                    {t(
                        'Explore options for {{modelName}}. Compare price, condition, accessories and performance to find your best match.',
                        { modelName }
                    )}
                </p>
                <ScrollableCarousel
                    className="model-overview-other-stock-product-list"
                    items={otherStock.map((product, index) => (
                        <MiniProductCard
                            {...product}
                            key={product.sku}
                            data-testid={`${dataTestId}__other-stock-product-${index}`}
                            className="model-overview-other-stock-product"
                            urlSegment={modelUrlSegment}
                            name={modelName}
                        />
                    ))}
                    itemOffset={0}
                    initialItemIndex={otherStock.findIndex(
                        ({ isCurrentProduct }) => isCurrentProduct
                    )}
                />
                <Button
                    className="model-overview-other-stock-comparison-button"
                    data-testid={`${dataTestId}__other-stock-comparison-button`}
                    variant={ButtonVariant.SECONDARY}
                    onClick={showProductComparison}
                >
                    {t('Compare')}
                </Button>
            </div>
        );
    };

    return (
        <StyledModelOverview
            hasImages={hasImages}
            isLoadingImages={isLoadingImages}
            hasSpecs={hasSpecs}
            ref={ref}
            data-testid={dataTestId}
            {...otherProps}
        >
            <ConstrainedWrapper type={ConstrainedWrapperType.PADDING}>
                <div className="model-overview-wrapper">
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {isSmallTablet && modelOverviewHeading}
                    <div className="model-overview-left">
                        {!isSmallTablet && modelOverviewHeading}
                        {renderDescription()}
                        {renderOtherStockProducts()}
                    </div>
                    {renderCarousel()}
                </div>
            </ConstrainedWrapper>

            <div ref={specsRef} className="model-overview-specs">
                {renderSpecSection()}
            </div>
        </StyledModelOverview>
    );
};

export const ModelOverview = forwardRef(ModelOverviewWithRef);
